import React, { createContext, useCallback, useContext, useState } from 'react';

// Définir l'interface pour le contexte
interface FullscreenContextProps {
  isFullscreen: boolean;
  enterFullscreen: () => void;
  exitFullscreen: () => void;
}

// Créer le contexte avec des valeurs par défaut
const FullscreenContext = createContext<FullscreenContextProps>({
  isFullscreen: false,
  enterFullscreen: () => {},
  exitFullscreen: () => {},
});

// Exporter un hook pour accéder facilement au contexte
export const useFullscreen = () => useContext(FullscreenContext);

// Provider du contexte pour encapsuler l'application
export const FullscreenProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Fonction pour entrer en mode plein écran
  const enterFullscreen = useCallback(() => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if ((elem as any).mozRequestFullScreen) {
      (elem as any).mozRequestFullScreen();
    } else if ((elem as any).webkitRequestFullscreen) {
      (elem as any).webkitRequestFullscreen();
    } else if ((elem as any).msRequestFullscreen) {
      (elem as any).msRequestFullscreen();
    }
    setIsFullscreen(true);
  }, []);

  // Fonction pour sortir du mode plein écran
  const exitFullscreen = useCallback(() => {
    if (document.exitFullscreen) {
      document.exitFullscreen();
    } else if ((document as any).mozCancelFullScreen) {
      (document as any).mozCancelFullScreen();
    } else if ((document as any).webkitExitFullscreen) {
      (document as any).webkitExitFullscreen();
    } else if ((document as any).msExitFullscreen) {
      (document as any).msExitFullscreen();
    }
    setIsFullscreen(false);
  }, []);

  // Fournir le contexte aux enfants
  return (
    <FullscreenContext.Provider
      value={{ isFullscreen, enterFullscreen, exitFullscreen }}
    >
      {children}
    </FullscreenContext.Provider>
  );
};
